package fr.frux.core.network.auth

import fr.frux.core.common.auth.TokenStorage
import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.util.*

/**
 * Marque une requête qui est effectivement partie avec un token.
 *
 * C'est ce qui distingue un 401 « session morte » d'un 401 « mauvais identifiants » : le second
 * arrive sur une requête anonyme (`POST /api/auth/login`) et ne doit rien terminer du tout (#48).
 */
val AuthenticatedRequestAttribute = AttributeKey<Unit>("FruxAuthenticatedRequest")

/**
 * Ktor plugin to automatically inject JWT token into requests.
 * Adds "Authorization: Bearer <token>" header to requests targeting the Frux API host only.
 *
 * Le client Ktor partagé est aussi celui de Coil (App.kt) : une requête image vers un hôte tiers
 * (CDN photos, `PHOTOS_PUBLIC_BASE_URL`) ne doit ni recevoir le JWT ni pouvoir déclencher
 * l'expiration de session sur un 401 qui n'a rien à voir avec l'API.
 */
class AuthInterceptor(
    private val tokenStorage: TokenStorage,
    private val tokenRefresher: TokenRefresher,
    private val apiHost: Url,
) {
    class Config {
        var tokenStorage: TokenStorage? = null
        var tokenRefresher: TokenRefresher? = null
        var apiHost: Url? = null
    }

    companion object Plugin : HttpClientPlugin<Config, AuthInterceptor> {
        override val key: AttributeKey<AuthInterceptor> = AttributeKey("AuthInterceptor")

        override fun prepare(block: Config.() -> Unit): AuthInterceptor {
            val config = Config().apply(block)
            return AuthInterceptor(
                tokenStorage =
                    config.tokenStorage
                        ?: error("TokenStorage must be provided to AuthInterceptor"),
                tokenRefresher =
                    config.tokenRefresher
                        ?: error("TokenRefresher must be provided to AuthInterceptor"),
                apiHost =
                    config.apiHost
                        ?: error("apiHost must be provided to AuthInterceptor"),
            )
        }

        override fun install(
            plugin: AuthInterceptor,
            scope: HttpClient,
        ) {
            scope.requestPipeline.intercept(HttpRequestPipeline.State) {
                if (!plugin.isApiRequest(context.url)) return@intercept
                val stored = plugin.tokenStorage.load() ?: return@intercept
                // Proactif (#26, ADR 0019) : un access qui expire dans moins de 60 s serait un 401
                // garanti au réveil de l'app. `null` = session morte détectée ici, la requête part
                // sans jeton plutôt qu'avec un access qu'on sait déjà refusé.
                val tokens = plugin.tokenRefresher.refreshIfExpiringSoon(stored) ?: return@intercept
                context.header(HttpHeaders.Authorization, "Bearer ${tokens.accessToken}")
                context.attributes.put(AuthenticatedRequestAttribute, Unit)
            }
        }
    }

    // `.build()` avant de comparer : `URLBuilder.port` brut vaut 0 tant que l'URL n'a pas précisé
    // de port explicite, alors que `Url.port` résout déjà le port par défaut du protocole (443
    // pour https). Comparer les deux bruts ferait rater tout hôte API en https sans port explicite.
    private fun isApiRequest(requestUrl: URLBuilder): Boolean {
        val resolved = requestUrl.build()
        return resolved.host == apiHost.host &&
            resolved.port == apiHost.port &&
            !isAnonymousAuthRequest(resolved.encodedPath)
    }

    /**
     * Routes d'auth anonymes exclues du proactif (#26, ADR 0019, C4a) : elles ne portent jamais de
     * JWT (le corps porte le refresh token opaque, pas un `Authorization`) et c'est justement là
     * que se joue la course avec `SessionStore.onSessionExpired` — un `logout` explicite ne doit
     * pas déclencher au passage un refresh proactif sur un access déjà en train d'être révoqué.
     * `/auth/refresh` ne transite jamais par ce client (client minimal dédié), l'exemption reste
     * ici pour être correcte par construction si ça changeait. `/auth/logout-all` en est exclue :
     * elle exige un JWT valide.
     */
    private fun isAnonymousAuthRequest(encodedPath: String): Boolean =
        encodedPath.endsWith("/auth/request-code") ||
            encodedPath.endsWith("/auth/verify-code") ||
            encodedPath.endsWith("/auth/refresh") ||
            encodedPath.endsWith("/auth/logout")
}

/**
 * Extension function to configure Auth interceptor.
 */
internal fun HttpClientConfig<*>.installAuth(
    tokenStorage: TokenStorage,
    tokenRefresher: TokenRefresher,
    apiBaseUrl: String,
) {
    install(AuthInterceptor) {
        this.tokenStorage = tokenStorage
        this.tokenRefresher = tokenRefresher
        this.apiHost = Url(apiBaseUrl)
    }
}
